(function () {
  if (window.__headingHighlightComponentLoaded) return;
  window.__headingHighlightComponentLoaded = true;

  const COUNTUP_SELECTOR = '[data-highlight-style="countup"] em, [data-highlight-style="countup"] i';
  const IMAGE_ROOT_SELECTOR = '[data-highlight-image-enabled="true"]';
  const IMAGE_TARGET_SELECTOR = 'em, i';
  const imageHighlightCleanups = new WeakMap();
  const DRAW_STYLES = [
    'ink_slash',
    'marker',
    'outline',
    'brush_box',
    'soft_underline',
    'circle_sketch',
    'double_underline',
    'corner_marks',
    'soft_glow',
    'ribbon',
    'brackets',
    'dotted_underline',
    'shadow_pop',
    'stamp_frame',
    'neon_edge',
    'split_tone',
    'vertical_bar',
    'overline_sweep',
    'pixel_block',
    'spark_dots',
    'slash_cut',
    'capsule_fill',
    'offset_plate',
    'swipe',
    'sheen',
    'halo'
  ];
  const DRAW_SELECTOR = DRAW_STYLES
    .flatMap((style) => [
      `[data-highlight-style="${style}"] em`,
      `[data-highlight-style="${style}"] i`
    ])
    .join(', ');
  const REVEAL_OBSERVER_OPTIONS = {
    rootMargin: '0px 0px -35% 0px',
    threshold: 0.2
  };
  const DURATION = 2200;

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function parseNumber(text) {
    const match = String(text || '').trim().match(/^([^0-9-]*)(-?[0-9][0-9,.]*)(.*)$/);
    if (!match) return null;

    const rawNumber = match[2];
    const decimalPart = rawNumber.split('.')[1];
    const decimals = decimalPart ? decimalPart.length : 0;
    const value = Number(rawNumber.replace(/,/g, ''));

    if (!Number.isFinite(value)) return null;

    return {
      prefix: match[1] || '',
      suffix: match[3] || '',
      value,
      decimals,
      useGrouping: rawNumber.includes(',')
    };
  }

  function formatNumber(parts, value) {
    const rounded = value.toFixed(parts.decimals);
    const numeric = parts.useGrouping
      ? Number(rounded).toLocaleString(undefined, {
          minimumFractionDigits: parts.decimals,
          maximumFractionDigits: parts.decimals
        })
      : rounded;

    return `${parts.prefix}${numeric}${parts.suffix}`;
  }

  function easeInOutCubic(value) {
    return value < 0.5
      ? 4 * value * value * value
      : 1 - Math.pow(-2 * value + 2, 3) / 2;
  }

  function animateCountup(element) {
    if (!element || element.dataset.headingHighlightCountupDone === 'true') return;

    const original = element.dataset.headingHighlightCountupOriginal || element.textContent;
    const parts = parseNumber(original);
    if (!parts) return;

    element.dataset.headingHighlightCountupOriginal = original;
    element.dataset.headingHighlightCountupDone = 'true';

    if (prefersReducedMotion()) {
      element.textContent = original;
      return;
    }

    const start = performance.now();
    const startValue = parts.value < 15 ? 0 : parts.value - 15;
    const tick = (now) => {
      const progress = Math.min((now - start) / DURATION, 1);
      const eased = easeInOutCubic(progress);
      element.textContent = formatNumber(parts, startValue + ((parts.value - startValue) * eased));

      if (progress < 1) {
        window.requestAnimationFrame(tick);
      } else {
        element.textContent = original;
      }
    };

    element.textContent = formatNumber(parts, startValue);
    window.requestAnimationFrame(tick);
  }

  function revealDrawHighlight(element) {
    if (!element || element.dataset.headingHighlightDrawVisible === 'true') return;

    element.dataset.headingHighlightDrawVisible = 'true';
    element.classList.remove('heading-highlight-draw-pending');
    element.classList.add('heading-highlight-draw-visible');
  }

  function initDrawHighlights(scope) {
    const targets = Array.from((scope || document).querySelectorAll(DRAW_SELECTOR))
      .filter((target) => target.dataset.headingHighlightDrawBound !== 'true');

    if (!targets.length) return;

    targets.forEach((target) => {
      target.dataset.headingHighlightDrawBound = 'true';
    });

    if (prefersReducedMotion()) {
      targets.forEach(revealDrawHighlight);
      return;
    }

    targets.forEach((target) => {
      target.classList.add('heading-highlight-draw-pending');
    });

    if (!('IntersectionObserver' in window)) {
      targets.forEach(revealDrawHighlight);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        revealDrawHighlight(entry.target);
        observer.unobserve(entry.target);
      });
    }, REVEAL_OBSERVER_OPTIONS);

    targets.forEach((target) => observer.observe(target));
  }

  function initCountup(scope) {
    const targets = Array.from((scope || document).querySelectorAll(COUNTUP_SELECTOR));
    if (!targets.length) return;

    if (!('IntersectionObserver' in window)) {
      targets.forEach(animateCountup);
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        animateCountup(entry.target);
        observer.unobserve(entry.target);
      });
    }, REVEAL_OBSERVER_OPTIONS);

    targets.forEach((target) => observer.observe(target));
  }

  function initImageHighlights(scope) {
    if (!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches) return;

    const roots = Array.from((scope || document).querySelectorAll(IMAGE_ROOT_SELECTOR))
      .filter((root) => root.dataset.headingHighlightImageBound !== 'true');

    if (!roots.length) return;

    roots.forEach((root) => {
      const preview = root.querySelector('[data-highlight-image]');
      if (!preview || !root.querySelector(IMAGE_TARGET_SELECTOR)) return;

      root.dataset.headingHighlightImageBound = 'true';
      document.body.appendChild(preview);

      let activeTarget = null;
      let pointerX = 0;
      let pointerY = 0;
      let frameId = 0;

      const hide = () => {
        activeTarget = null;
        preview.removeAttribute('data-visible');
        if (!frameId) return;
        window.cancelAnimationFrame(frameId);
        frameId = 0;
      };

      const updatePosition = () => {
        frameId = 0;
        if (!activeTarget) return;

        const offset = 16;
        const edge = 8;
        const width = preview.offsetWidth;
        const height = preview.offsetHeight;
        const x = Math.min(Math.max(edge, pointerX + offset), window.innerWidth - width - edge);
        const y = Math.min(Math.max(edge, pointerY + offset), window.innerHeight - height - edge);

        preview.style.setProperty('--heading-highlight-image-x', `${x}px`);
        preview.style.setProperty('--heading-highlight-image-y', `${y}px`);
      };

      const queuePosition = (event) => {
        pointerX = event.clientX;
        pointerY = event.clientY;
        if (frameId) return;
        frameId = window.requestAnimationFrame(updatePosition);
      };

      const getHighlightTarget = (element) => {
        if (!(element instanceof Element)) return null;
        const target = element.closest(IMAGE_TARGET_SELECTOR);
        return target && root.contains(target) ? target : null;
      };

      const handlePointerOver = (event) => {
        const target = getHighlightTarget(event.target);
        if (!target || !root.contains(target)) return;

        activeTarget = target;
        queuePosition(event);
        preview.setAttribute('data-visible', 'true');
      };

      const handlePointerMove = (event) => {
        if (!activeTarget) return;
        queuePosition(event);
      };

      const handlePointerOut = (event) => {
        const target = getHighlightTarget(event.target);
        if (!target || target !== activeTarget) return;
        if (event.relatedTarget && target.contains(event.relatedTarget)) return;

        const nextTarget = getHighlightTarget(event.relatedTarget);
        if (nextTarget) {
          activeTarget = nextTarget;
          return;
        }

        hide();
      };

      root.addEventListener('pointerover', handlePointerOver);
      root.addEventListener('pointermove', handlePointerMove);
      root.addEventListener('pointerout', handlePointerOut);

      root.addEventListener('pointerleave', hide);
      root.addEventListener('pointercancel', hide);

      imageHighlightCleanups.set(root, () => {
        hide();
        root.removeEventListener('pointerover', handlePointerOver);
        root.removeEventListener('pointermove', handlePointerMove);
        root.removeEventListener('pointerout', handlePointerOut);
        root.removeEventListener('pointerleave', hide);
        root.removeEventListener('pointercancel', hide);
        delete root.dataset.headingHighlightImageBound;
        preview.remove();
        imageHighlightCleanups.delete(root);
      });
    });
  }

  function cleanupImageHighlights(scope) {
    const roots = Array.from((scope || document).querySelectorAll(IMAGE_ROOT_SELECTOR));
    roots.forEach((root) => imageHighlightCleanups.get(root)?.());
  }

  function init(scope) {
    initDrawHighlights(scope);
    initCountup(scope);
    initImageHighlights(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupImageHighlights(event.target));
})();
